import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CourseChrome from "../components/CourseChrome.js";
import LibraryPicker from "../components/LibraryPicker.js";
import {
  api,
  importFromLibrary,
  termLabel,
  type Course,
  type ImportDestination,
  type ImportTargetCourse,
} from "../api.js";

/**
 * Question library: reuse work from previous semesters, and the stock question
 * bank that ships with the app.
 *
 * Importing copies. The copy is independent, so revising it never alters the
 * original or the exam papers and grades that were built on it.
 *
 * The destination is a container, not just a course. A library question is a
 * question, and dropping every import at the top of the assignment list left
 * teachers with a row of loose one-question assignments to file by hand -- so
 * the sheets and exam papers that will actually hold them are offered here
 * directly.
 */
export default function Library() {
  // The course you arrived from, carried in the URL by the tab bar. It keeps
  // the header consistent and pre-selects the obvious import destination.
  const [params] = useSearchParams();
  const fromCourse = params.get("course");

  const [courses, setCourses] = useState<Course[]>([]);
  const [targets, setTargets] = useState<ImportTargetCourse[]>([]);
  /** The destination, encoded for the select: "course:ID", "sheet:ID", "exam:ID". */
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ courses: Course[] }>("/api/courses")
      .then((r) => setCourses(r.courses.filter((c) => c.role === "TEACHER")))
      .catch((e) => setError(e.message));

    api
      .get<{ courses: ImportTargetCourse[] }>("/api/library/targets")
      .then((r) => {
        setTargets(r.courses);
        // Default to the course you came from: importing into the one you were
        // just looking at is the whole reason you opened the library.
        const preferred = r.courses.find((c) => c.id === fromCourse) ?? r.courses[0];
        if (preferred) setTarget((t) => t || `course:${preferred.id}`);
      })
      .catch((e) => setError(e.message));
  }, []);

  const [kind, id] = target.split(":");
  const destination: ImportDestination | null =
    kind === "course" ? { courseId: id } : kind === "sheet" ? { sheetId: id } : kind === "exam" ? { examId: id } : null;

  /** The course the chosen destination belongs to, for the "open it" link. */
  const destCourse = targets.find(
    (c) =>
      (kind === "course" && c.id === id) ||
      (kind === "sheet" && c.sheets.some((s) => s.id === id)) ||
      (kind === "exam" && c.exams.some((e) => e.id === id))
  );

  async function handleImport(ids: string[]): Promise<string[]> {
    if (!destination) {
      setError("Choose where the questions should go first.");
      return [];
    }
    setError(null);
    setStatus(null);
    try {
      const r = await importFromLibrary(destination, ids);
      const cases = r.imported.reduce((n, q) => n + q.testCasesCopied, 0);
      const where =
        kind === "sheet"
          ? `“${destCourse?.sheets.find((s) => s.id === id)?.title ?? "the sheet"}”`
          : kind === "exam"
          ? `“${destCourse?.exams.find((e) => e.id === id)?.title ?? "the exam"}”`
          : `${destCourse?.code ?? "the course"} ${destCourse ? termLabel(destCourse) : ""}`.trim();
      setStatus(
        (r.imported.length === 1
          ? `Copied "${r.imported[0].title}" (${cases} test cases) into ${where}.`
          : `Copied ${r.imported.length} questions (${cases} test cases) into ${where}.`) +
          (r.skipped.length ? ` ${r.skipped.length} skipped: ${r.skipped[0].reason}` : "") +
          // Only a loose copy needs publishing by hand; inside a sheet or on a
          // paper the container already decides who sees it and when.
          (kind === "course" ? " They are unpublished — review, then publish." : "")
      );
      return r.imported.map((q) => q.sourceId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed.");
      return [];
    }
  }

  return (
    <div className="page">
      <CourseChrome section="library" course={courses.find((c) => c.id === fromCourse) ?? null} />

      {error && <div className="error-banner">{error}</div>}
      {/* The copies land in another page, so the confirmation carries the way
          there -- otherwise a teacher who has just imported eight questions has
          to go and find them. */}
      {status && (
        <div className="notice">
          {status}{" "}
          {destCourse && <Link to={`/assignments?course=${destCourse.id}`}>Open the course →</Link>}
        </div>
      )}

      <LibraryPicker
        onImport={handleImport}
        importDisabled={!destination}
        onError={setError}
        extraFilter={
          <label style={{ marginBottom: 0, flex: 1, minWidth: 260 }}>
            <span>Add to</span>
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              {targets.length === 0 && <option value="">You teach no courses</option>}
              {targets.map((c) => (
                <optgroup key={c.id} label={`${c.code} — ${termLabel(c)}`}>
                  <option value={`course:${c.id}`}>As its own assignment</option>
                  {c.sheets.map((s) => (
                    <option key={s.id} value={`sheet:${s.id}`}>
                      {s.title} ({s.questionCount} question{s.questionCount === 1 ? "" : "s"})
                    </option>
                  ))}
                  {c.exams.map((e) => (
                    // A sat paper is frozen, so it is shown and refused rather
                    // than quietly missing from a list the teacher expects it in.
                    <option key={e.id} value={`exam:${e.id}`} disabled={e.frozen}>
                      {e.title} — exam{e.frozen ? " (sat, frozen)" : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        }
      />
    </div>
  );
}
